import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { useLocation, useNavigate } from 'react-router-dom'
import { useKey } from 'react-use'
import { useAppSelector } from '../../../store/hooks'
import { selectCrossClientLandingDecompositionResult, selectCrossClientInitLoading } from '../../../store/crossClient/crossClientLanding'
import { type CrossClientQuerySuggestedDecomposition, type PredefinedCrossClientQuery } from '../../../models/CrossClient'
import { CrossClientLandingTabs } from './Tabs/CrossClientLandingTabs'
import { CrossClientLandingInitialization } from './CrossClientLandingInitialization'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { CROSS_CLIENT_FUNDS_START_BLOCKED_MESSAGE } from '../utils/crossClientFunds'
import { useCrossClientFundsQuotas } from '../utils/crossClientFundsQuotas'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { useCrossClientQueryLandingExecution, MAX_USER_QUESTION_LENGTH, reviewKeepsSubmittedSettings } from './hooks'
import { useCrossClientLandingPrefill } from './landingPrefill'
import { useCrossClientQuerySettings } from './crossClientSettings'
import { CrossClientQueryInput } from './CrossClientQueryInput'
import { CrossClientChatReview, CrossClientChatReviewActions } from './CrossClientChatReview'
import { CrossClientClientsPill } from './Pills/CrossClientClientsPill'
import { CrossClientSourcesPill } from './Pills/CrossClientSourcesPill'
import { useRecommendedCrossClientQueries } from '../Predefined/useRecommendedCrossClientQueries'
import { RECOMMENDED_SAMPLE_SIZE, pickRandomSample, shouldShowRecommendedQueries } from './recommendedSample'
import { CcqInitPage } from '../Results/Run/CcqInitState'
import {
  type DecompositionExitPoint,
  DecompositionExitPoints,
  dismissEventName,
  exitEventDetails,
  exitPointForReviewState,
  suggestionCardType,
} from './decompositionAnalytics'
import './CrossClientLanding.scss'

export const CrossClientLanding = () => {
  const { tracked, trackingClient } = useTracking()
  const { hasScope } = useZocksAuth()

  const { state: locationState, search: locationSearch } = useLocation()
  const navigate = useNavigate()

  const isAdmin = hasScope('**', 'cross_client_query:admin', [])

  const decompositionResult = useAppSelector(selectCrossClientLandingDecompositionResult)
  const initLoading = useAppSelector(selectCrossClientInitLoading)

  const { canStart, quotaLoading } = useCrossClientFundsQuotas()

  const prefill = useCrossClientLandingPrefill()
  const [userQuestion, setUserQuestion] = useState<string>(prefill.question ?? '')
  const [wasPredefined, setWasPredefined] = useState<boolean>(false)
  const [pendingPredefined, setPendingPredefined] = useState<PredefinedCrossClientQuery | null>(null)
  const { sources, sourcesReady, setSources, clientQueryMode, setClientQueryMode } = useCrossClientQuerySettings({
    initialSources: prefill.sources,
    initialClientQueryMode: prefill.clientQueryMode,
  })

  const {
    reviewState,
    startingQuery,
    decomposeCrossClientQuery,
    runSelectedDecomposition,
    previewPredefinedQuery,
    confirmStart,
    retryStart,
    resetExecution,
    cancelDecomposition,
    decompositionContextRef,
  } = useCrossClientQueryLandingExecution()

  const suggestionShownAtRef = useRef<number | null>(null)
  useEffect(() => {
    suggestionShownAtRef.current = suggestionCardType(reviewState) !== null ? performance.now() : null
  }, [reviewState])
  const timeToDecisionMs = useCallback(() => (suggestionShownAtRef.current === null ? null : Math.round(performance.now() - suggestionShownAtRef.current)), [])

  const showRecommendedQueries = shouldShowRecommendedQueries(reviewState)
  const { queries: allRecommendedQueries } = useRecommendedCrossClientQueries({ skip: !showRecommendedQueries })
  const recommendedQueries = useMemo(
    () => (showRecommendedQueries ? pickRandomSample(allRecommendedQueries, RECOMMENDED_SAMPLE_SIZE) : []),
    [showRecommendedQueries, allRecommendedQueries],
  )

  const atomicClauses = decompositionResult?.decomposition?.atomicClauses ?? []
  const numberOfClauses = atomicClauses.length
  const fundsBlocked = !canStart
  const cannotExecuteNew = fundsBlocked || quotaLoading || !sourcesReady
  const buttonDisabled = cannotExecuteNew || userQuestion.trim().length < 3 || userQuestion.trim().length > MAX_USER_QUESTION_LENGTH
  const isWorking = initLoading
  const isReviewing = !isWorking && (!!reviewState || numberOfClauses > 0)
  // The decomposition result and loading flag live in the store and render a beat before the local reviewState lands.
  // Keep the working row up until the answer exists, so the card doesn't shrink to the bare question in between.
  const isAwaitingReview = isReviewing && !reviewState
  const inputMode = isWorking || isAwaitingReview ? 'working' : isReviewing ? 'review' : 'input'
  const chatCardStateClass = isWorking
    ? 'cross-client-chat-card--working'
    : isReviewing
      ? 'cross-client-chat-card--review'
      : cannotExecuteNew
        ? 'cross-client-chat-card--disabled'
        : userQuestion.trim().length > 0
          ? 'cross-client-chat-card--typed'
          : ''
  const showBackdrop = isWorking || isReviewing
  // Pill changes are only picked up where the next run reads them; elsewhere they'd be silently ignored.
  const pillsDisabled = cannotExecuteNew || inputMode === 'working' || reviewKeepsSubmittedSettings(reviewState)

  const triggerPredefinedDecompose = useCallback(
    async (predefinedQuery: PredefinedCrossClientQuery) => {
      setUserQuestion(predefinedQuery.question)
      setWasPredefined(true)
      navigate(`/cross-client${locationSearch}`, { state: { queryInProgress: true } })
      await decomposeCrossClientQuery({
        userQuestion: predefinedQuery.question,
        sources,
        clientQueryMode,
        predefinedQueryId: predefinedQuery.id,
        decomposition: predefinedQuery.decomposition,
        nnfDecomposition: predefinedQuery.nnfDecomposition ?? undefined,
      })
    },
    [decomposeCrossClientQuery, navigate, sources, clientQueryMode, locationSearch],
  )

  const restart = useCallback(() => {
    resetExecution()
    setUserQuestion('')
    setWasPredefined(false)
    setPendingPredefined(null)
    navigate(`/cross-client${locationSearch}`, { replace: true, state: null })
  }, [resetExecution, navigate, locationSearch])

  const trackQueryDenied = useCallback(
    (exitFrom: DecompositionExitPoint | null) => {
      const dismissed = dismissEventName(reviewState)
      if (dismissed) {
        trackingClient.track({
          eventName: dismissed,
          eventDetails: {
            suggestionType: suggestionCardType(reviewState),
            timeToDecisionMs: timeToDecisionMs(),
            query: userQuestion,
            ...exitEventDetails(decompositionContextRef.current, exitFrom),
          },
        })
      }
      trackingClient.track({
        eventName: TrackEvent.X_CLIENT_QUERY_DENIED,
        eventDetails: {
          type: wasPredefined ? 'pre-defined' : 'custom',
          query: userQuestion,
          ...exitEventDetails(decompositionContextRef.current, exitFrom),
        },
      })
    },
    [reviewState, trackingClient, timeToDecisionMs, userQuestion, decompositionContextRef, wasPredefined],
  )

  const handleCancel = useCallback(() => {
    trackQueryDenied(reviewState ? exitPointForReviewState(reviewState) : DecompositionExitPoints.DECOMPOSING)
    cancelDecomposition()
    restart()
  }, [trackQueryDenied, reviewState, cancelDecomposition, restart])

  // Once Find clients / Retry starts the query the init screen takes over and the start request can't be aborted.
  const canCancel = inputMode !== 'input' && !startingQuery
  useKey(
    'Escape',
    () => {
      // Let Escape close an open popover/menu or modal first instead of also discarding the query. react-bootstrap
      // closes those on keyup, so this keydown can't tell from the event itself.
      const hasOpenOverlay = document.querySelector('.dropdown-menu.show, .modal.show') !== null
      if (canCancel && !hasOpenOverlay) {
        handleCancel()
      }
    },
    undefined,
    [canCancel, handleCancel],
  )

  const handleRestart = useCallback(() => {
    trackQueryDenied(exitPointForReviewState(reviewState))
    // Clear the execution synchronously before navigating so the state-sync effect doesn't see a stale
    // decompositionResult and fire restart() (which would also wipe the question we want to keep).
    resetExecution()
    setWasPredefined(false)
    setPendingPredefined(null)
    navigate(`/cross-client${locationSearch}`, { replace: true, state: null })
  }, [trackQueryDenied, reviewState, resetExecution, navigate, locationSearch])

  const handleInitializeCustomCrossClientQuery = tracked(
    async () => {
      setWasPredefined(false)
      setPendingPredefined(null)
      navigate('/cross-client', { state: { queryInProgress: true } })
      await decomposeCrossClientQuery({
        userQuestion,
        sources,
        clientQueryMode,
      })
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_SUBMITTED,
      eventDetails: {
        type: 'custom',
        query: userQuestion,
        sources: sources.map((source) => (source.type === 'integration' ? source.integrationType : source.type)),
        prefilled: prefill.question !== undefined && userQuestion === prefill.question,
      },
    },
  )

  const handleUseOriginal = useCallback(async () => {
    if (reviewState?.kind !== 'refinement') {
      return
    }
    trackingClient.track({
      eventName: TrackEvent.X_CLIENT_SUGGESTION_DECLINED,
      eventDetails: {
        suggestionType: suggestionCardType(reviewState),
        timeToDecisionMs: timeToDecisionMs(),
        query: userQuestion,
        suggestedQuestion: reviewState.suggestion.userQuestion,
        ...exitEventDetails(decompositionContextRef.current, DecompositionExitPoints.SUGGESTION_CARD),
      },
    })
    setWasPredefined(false)
    setPendingPredefined(null)
    await runSelectedDecomposition(userQuestion, reviewState.decomposition, reviewState.nnfDecomposition, sources, clientQueryMode)
  }, [reviewState, trackingClient, timeToDecisionMs, userQuestion, decompositionContextRef, runSelectedDecomposition, sources, clientQueryMode])

  const handleUseSuggested = useCallback(
    async (suggestion: CrossClientQuerySuggestedDecomposition) => {
      trackingClient.track({
        eventName: TrackEvent.X_CLIENT_SUGGESTION_ACCEPTED,
        eventDetails: {
          suggestionType: suggestionCardType(reviewState),
          timeToDecisionMs: timeToDecisionMs(),
          query: userQuestion,
          suggestedQuestion: suggestion.userQuestion,
          ...exitEventDetails(decompositionContextRef.current, DecompositionExitPoints.SUGGESTION_CARD),
        },
      })
      setWasPredefined(false)
      setPendingPredefined(null)
      setUserQuestion(suggestion.userQuestion)
      await runSelectedDecomposition(suggestion.userQuestion, suggestion.decomposition, suggestion.nnfDecomposition, sources, clientQueryMode)
    },
    [trackingClient, reviewState, timeToDecisionMs, userQuestion, decompositionContextRef, runSelectedDecomposition, sources, clientQueryMode],
  )

  const handleEditQuestionText = useCallback(
    (question: string) => {
      trackingClient.track({
        eventName: TrackEvent.X_CLIENT_SUGGESTION_EDITED,
        eventDetails: {
          suggestionType: suggestionCardType(reviewState),
          timeToDecisionMs: timeToDecisionMs(),
          query: userQuestion,
          suggestedQuestion: question,
          ...exitEventDetails(decompositionContextRef.current, DecompositionExitPoints.SUGGESTION_CARD),
        },
      })
      resetExecution()
      setUserQuestion(question)
      setWasPredefined(false)
      setPendingPredefined(null)
      navigate(`/cross-client${locationSearch}`, { replace: true, state: null })
    },
    [trackingClient, reviewState, timeToDecisionMs, userQuestion, decompositionContextRef, resetExecution, navigate, locationSearch],
  )

  const handleSeeAllRecommendedQueries = useCallback(() => {
    trackingClient.track({
      eventName: TrackEvent.X_CLIENT_QUERY_LIBRARY_BROWSED,
      eventDetails: {
        query: userQuestion,
        ...exitEventDetails(decompositionContextRef.current, DecompositionExitPoints.REJECTION_CARD),
      },
    })
    resetExecution()
    const nextSearchParams = new URLSearchParams(locationSearch)
    nextSearchParams.set('tab', 'recommended')
    navigate(`/cross-client?${nextSearchParams.toString()}`, { replace: true, state: null })
  }, [trackingClient, userQuestion, decompositionContextRef, resetExecution, locationSearch, navigate])

  // The pick opens the confirm card at once, so it counts as the submission, like a typed question.
  const handlePredefinedCrossClientQuerySelected = useCallback(
    (predefinedQuery: PredefinedCrossClientQuery) => {
      trackingClient.track({
        eventName: TrackEvent.X_CLIENT_QUERY_SUBMITTED,
        eventDetails: { type: 'pre-defined', query: predefinedQuery.question },
      })
      setUserQuestion(predefinedQuery.question)
      setWasPredefined(true)
      setPendingPredefined(predefinedQuery)
      previewPredefinedQuery(predefinedQuery, sources, clientQueryMode)
    },
    [trackingClient, previewPredefinedQuery, sources, clientQueryMode],
  )

  const handleFindClients = useCallback(() => confirmStart({ sources, clientQueryMode }), [confirmStart, sources, clientQueryMode])

  const handleRecommendedQuerySelectedFromCard = useCallback(
    (predefinedQuery: PredefinedCrossClientQuery) => {
      trackingClient.track({
        eventName: TrackEvent.X_CLIENT_RECOMMENDED_QUERY_SELECTED,
        eventDetails: {
          entryPoint: 'review_card',
          predefinedQueryId: predefinedQuery.id,
          title: predefinedQuery.title,
          query: predefinedQuery.question,
          ...exitEventDetails(decompositionContextRef.current, DecompositionExitPoints.REJECTION_CARD),
        },
      })
      handlePredefinedCrossClientQuerySelected(predefinedQuery)
    },
    [trackingClient, decompositionContextRef, handlePredefinedCrossClientQuerySelected],
  )

  const handleRecommendedQuerySelectedFromTab = useCallback(
    (predefinedQuery: PredefinedCrossClientQuery) => {
      trackingClient.track({
        eventName: TrackEvent.X_CLIENT_RECOMMENDED_QUERY_SELECTED,
        eventDetails: {
          entryPoint: 'recommended_tab',
          predefinedQueryId: predefinedQuery.id,
          title: predefinedQuery.title,
          query: predefinedQuery.question,
          ...exitEventDetails(decompositionContextRef.current, DecompositionExitPoints.RECOMMENDED_TAB),
        },
      })
      handlePredefinedCrossClientQuerySelected(predefinedQuery)
    },
    [trackingClient, decompositionContextRef, handlePredefinedCrossClientQuerySelected],
  )

  const handleStartPredefinedCrossClientQuery = tracked(
    async (predefinedQuery: PredefinedCrossClientQuery) => {
      await triggerPredefinedDecompose(predefinedQuery)
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_SUBMITTED,
      eventDetails: {
        type: 'pre-defined',
      },
    },
    {
      eventDetailsExtractor: (predefinedQuery: PredefinedCrossClientQuery) => {
        return { query: predefinedQuery.question }
      },
    },
  )

  const handleSubmit = () => {
    if (pendingPredefined && pendingPredefined.question === userQuestion) {
      return handleStartPredefinedCrossClientQuery(pendingPredefined)
    }
    return handleInitializeCustomCrossClientQuery()
  }

  useEffect(() => {
    trackingClient.track({
      eventName: TrackEvent.X_CLIENT_OPENED,
      eventDetails: { prefilled: prefill.question !== undefined },
    })
  }, [trackingClient, prefill.question])

  useEffect(() => {
    if (!locationState?.queryInProgress && decompositionResult) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs local form state to the router: when navigating back to the landing without an in-progress query, discard the stale decomposition.
      restart()
    }
  }, [locationState?.queryInProgress, decompositionResult, restart])

  if (startingQuery) {
    return (
      <>
        <CrossClientLandingInitialization />
        <CcqInitPage question={startingQuery.question} criteria={startingQuery.criteria} shownSince={startingQuery.shownSince} />
      </>
    )
  }

  return (
    <>
      <CrossClientLandingInitialization />
      <div className='truly-full-size-content d-flex flex-column align-items-center position-relative cross-client-landing-container'>
        <div className='d-flex flex-column zocks-gap-2 w-100 cross-client-landing-content'>
          <div className='d-flex flex-column gap-1'>
            <h5 className='mb-0'>{isAdmin ? 'Team queries' : 'Your queries'}</h5>
            {isAdmin ? <div className='paragraph-small zocks-text-gray-700'>All queries across your team</div> : null}
          </div>
          <CrossClientLandingTabs isAdmin={isAdmin} runDisabled={cannotExecuteNew} onPredefinedQuerySelect={handleRecommendedQuerySelectedFromTab} />
        </div>
        {showBackdrop ? <div className='position-absolute top-0 start-0 end-0 bottom-0 z-1 cross-client-landing-backdrop' /> : null}
        <div className='cross-client-landing-dock position-sticky bottom-0 w-100 z-2 d-flex flex-column align-items-center mt-auto gap-2 px-3 zocks-pb-1.5'>
          <div className='position-relative w-100 cross-client-chat-shell'>
            {/* Always mounted so it can transition both in (rising from behind the card) and out. */}
            <button
              type='button'
              className={classNames(
                'position-absolute bottom-100 end-0 mb-2 d-inline-flex align-items-center gap-2 py-1 ps-2 pe-1 border zocks-border-color-gray-300 zocks-bg-color-white paragraph-small bold zocks-text-gray-900 cross-client-chat-cancel',
                { 'cross-client-chat-cancel--visible': canCancel },
              )}
              onClick={handleCancel}
              disabled={!canCancel}
              aria-keyshortcuts='Escape'
            >
              Cancel
              <kbd className='px-2 rounded-2 border zocks-border-color-gray-300 zocks-bg-color-white paragraph-extra-small medium zocks-text-gray-600 cross-client-chat-cancel__key'>
                Esc
              </kbd>
            </button>
            <div className={classNames('cross-client-chat-card', chatCardStateClass)}>
              <CrossClientQueryInput
                userQuestion={userQuestion}
                onQuestionChange={setUserQuestion}
                onSubmit={handleSubmit}
                disabled={cannotExecuteNew || initLoading || !!decompositionResult}
                submitDisabled={buttonDisabled}
                isLoading={initLoading}
                hasExecution={!!decompositionResult}
                mode={inputMode}
                reviewContent={
                  isReviewing && reviewState ? (
                    <CrossClientChatReview
                      reviewState={reviewState}
                      recommendedQueries={recommendedQueries}
                      onSelectRecommendedQuery={handleRecommendedQuerySelectedFromCard}
                      onSeeAllRecommendedQueries={handleSeeAllRecommendedQueries}
                      runDisabled={cannotExecuteNew || initLoading}
                    />
                  ) : null
                }
                footerRight={
                  isReviewing && reviewState ? (
                    <CrossClientChatReviewActions
                      reviewState={reviewState}
                      runDisabled={cannotExecuteNew}
                      onRetry={retryStart}
                      onRestart={handleRestart}
                      onFindClients={handleFindClients}
                      onEditQuestionText={handleEditQuestionText}
                      onUseOriginal={handleUseOriginal}
                      onUseSuggested={handleUseSuggested}
                    />
                  ) : null
                }
                footerLeft={
                  <>
                    <CrossClientClientsPill mode={clientQueryMode} onChange={setClientQueryMode} disabled={pillsDisabled} />
                    <CrossClientSourcesPill sources={sources} onChange={setSources} disabled={pillsDisabled} />
                  </>
                }
              />
            </div>
          </div>
          {fundsBlocked ? (
            <div className='d-flex gap-2 mt-2 py-1 px-2 zocks-bg-color-danger-background zocks-text-danger-200 zocks-border-0.5 align-items-center align-self-center'>
              <i className='bi bi-ban fs-6' />
              <div className='paragraph-small'>{CROSS_CLIENT_FUNDS_START_BLOCKED_MESSAGE}</div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  )
}
